package br.gov.go.pge.permanenciaweb.site.components

import androidx.compose.runtime.Composable
import br.gov.go.pge.permanenciaweb.site.IndustrialPalette
import com.varabyte.kobweb.compose.css.AlignItems
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.css.TextTransform
import com.varabyte.kobweb.compose.css.UserSelect
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.alignItems
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderBottom
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.display
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.letterSpacing
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.overflow
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.modifiers.textTransform
import com.varabyte.kobweb.compose.ui.modifiers.userSelect
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.extendedByBase
import com.varabyte.kobweb.silk.style.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.em
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H2


val PanelStyle = CssStyle.base {
    Modifier
        .backgroundColor(IndustrialPalette.White)
        // 1px hairline borders stay in px per the units policy.
        .border(1.px, LineStyle.Solid, IndustrialPalette.Border)
        .borderRadius(0.375.cssRem)
        .overflow(Overflow.Hidden)
}

val PanelTitleStyle = CssStyle.base {
    Modifier
        .fontSize(0.8125.cssRem)
        .textTransform(TextTransform.Uppercase)
        .letterSpacing(0.04.em)
        .color(IndustrialPalette.Muted)
        .margin(0.cssRem)
        .padding(0.75.cssRem, 1.cssRem)
        .borderBottom(1.px, LineStyle.Solid, IndustrialPalette.Grid)
}

val PanelEmptyStyle = CssStyle.base {
    Modifier.padding(1.cssRem).color(IndustrialPalette.Muted)
}

@Composable
fun Panel(modifier: Modifier = Modifier, content: @Composable () -> Unit) {
    Div(attrs = PanelStyle.toModifier().then(modifier).toAttrs()) {
        content()
    }
}

@Composable
fun PanelTitle(content: @Composable () -> Unit) {
    H2(attrs = PanelTitleStyle.toAttrs()) {
        content()
    }
}

/**
 * Panel header with the title on the left, an optional centred slot (the pager) and an optional
 * right-aligned actions slot. The two equal `1fr` side columns keep the auto-sized middle slot at
 * 50% of the panel width, without absolute positioning.
 */
val PanelHeaderStyle = CssStyle.base {
    Modifier
        .display(DisplayStyle.Grid)
        .styleModifier { property("grid-template-columns", "minmax(0, 1fr) auto minmax(0, 1fr)") }
        .alignItems(AlignItems.Center)
        .padding(0.75.cssRem, 1.cssRem)
        .borderBottom(1.px, LineStyle.Solid, IndustrialPalette.Grid)
}

/** [PanelTitleStyle] without its own padding/border; the header container owns them. */
val PanelHeaderTitleStyle = PanelTitleStyle.extendedByBase {
    Modifier
        .padding(0.cssRem)
        .styleModifier { property("border-bottom", "none") }
}

@Composable
fun PanelHeader(
    title: @Composable () -> Unit,
    center: @Composable () -> Unit = {},
    actions: @Composable () -> Unit = {},
) {
    Div(attrs = PanelHeaderStyle.toAttrs()) {
        H2(attrs = PanelHeaderTitleStyle.toAttrs()) { title() }
        Div(
            attrs = Modifier
                .display(DisplayStyle.Flex)
                .alignItems(AlignItems.Center)
                .styleModifier { property("justify-self", "center") }
                .toAttrs(),
        ) { center() }
        Div(
            attrs = Modifier
                .display(DisplayStyle.Flex)
                .alignItems(AlignItems.Center)
                .gap(0.5.cssRem)
                .styleModifier { property("justify-self", "end") }
                .toAttrs(),
        ) { actions() }
    }
}

/** Clickable variant of [PanelTitleStyle] for headers that toggle a collapsible section. */
val CollapsiblePanelTitleStyle = PanelTitleStyle.extendedByBase {
    Modifier
        .cursor(Cursor.Pointer)
        .userSelect(UserSelect.None)
}

/**
 * A [PanelTitle] that toggles a collapsible section: click or press Enter/Space, with the state
 * exposed to assistive tech via `aria-expanded`.
 */
@Composable
fun CollapsiblePanelTitle(expanded: Boolean, onToggle: () -> Unit, content: @Composable () -> Unit) {
    H2(
        attrs = CollapsiblePanelTitleStyle.toModifier().toAttrs {
            attr("aria-expanded", expanded.toString())
            tabIndex(0)
            onClick { onToggle() }
            onKeyDown { event ->
                if (event.key == "Enter" || event.key == " ") {
                    event.preventDefault()
                    onToggle()
                }
            }
        }
    ) {
        content()
    }
}
